import type {
  AdminMeDTO,
  AppointmentDTO,
  ApiResult,
  AuthMeDTO,
  ClientDTO,
  InvoiceDTO,
  JobDTO,
  JobChecklistItemDTO,
  LeadConversationAnalysisDTO,
  LeadDTO,
  MarketingContactDTO,
  MarketingContactEmailDiscoveryDTO,
  MediaAssetDTO,
  MessageTemplateDTO,
  OrganizationDTO,
  QuoteDTO,
  QuotePackageDTO,
  ServiceTargetDTO,
  UserDTO
} from "@tendvia/shared";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? "https://api.tendvia.com";
const DEFAULT_API_FALLBACK_URL = "https://tendvia.com";

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  let response: Response;
  try {
    response = await fetchApi(path, API_BASE_URL, init);
  } catch {
    if (API_BASE_URL === "https://api.tendvia.com") {
      try {
        response = await fetchApi(path, DEFAULT_API_FALLBACK_URL, init);
      } catch {
        throw new Error("TendVia is having trouble reaching the server. Please try again in a moment.");
      }
    } else {
      throw new Error("TendVia is having trouble reaching the server. Please try again in a moment.");
    }
  }

  let result: ApiResult<T>;
  try {
    result = (await response.json()) as ApiResult<T>;
  } catch {
    throw new Error("TendVia is having trouble reaching the server. Please try again in a moment.");
  }

  if (!result.ok) {
    throw new Error(result.error.message);
  }
  return result.data;
}

function fetchApi(path: string, baseUrl: string, init?: RequestInit): Promise<Response> {
  const headers = {
    ...(init?.body instanceof FormData ? {} : { "Content-Type": "application/json" }),
    ...init?.headers
  };

  if (baseUrl.startsWith("/")) {
    return fetch(path, {
      credentials: "include",
      headers,
      ...init
    });
  }

  return fetch(new URL(path, baseUrl), {
    credentials: "include",
    headers,
    ...init
  });
}

async function requestBlob(path: string): Promise<Blob> {
  let response: Response;
  try {
    response = await fetchApi(path, API_BASE_URL);
  } catch {
    if (API_BASE_URL !== "https://api.tendvia.com") {
      throw new Error("TendVia is having trouble reaching the server. Please try again in a moment.");
    }
    response = await fetchApi(path, DEFAULT_API_FALLBACK_URL);
  }

  if (!response.ok) {
    throw new Error("TendVia could not load that photo.");
  }

  return response.blob();
}

export const api = {
  me: () => request<AuthMeDTO>("/api/auth/me"),
  startOtp: (email: string) => request<{ sent: boolean; devCode?: string }>("/api/auth/otp/start", {
    method: "POST",
    body: JSON.stringify({ email })
  }),
  verifyOtp: (email: string, code: string, name?: string) => request<AuthMeDTO>("/api/auth/otp/verify", {
    method: "POST",
    body: JSON.stringify({ email, code, ...(name ? { name } : {}) })
  }),
  updateProfile: (name: string) => request<UserDTO>("/api/profile", {
    method: "PUT",
    body: JSON.stringify({ name })
  }),
  updateOrg: (input: { name?: string; vertical?: string; phone?: string | null; reviewLink?: string | null }) =>
    request<OrganizationDTO>("/api/org", {
      method: "PUT",
      body: JSON.stringify(input)
    }),
  messageTemplates: () => request<MessageTemplateDTO[]>("/api/message-templates"),
  updateMessageTemplate: (id: string, input: {
    title?: string;
    body?: string;
    status?: string;
    sortOrder?: number;
  }) =>
    request<MessageTemplateDTO>(`/api/message-templates/${id}`, {
      method: "PUT",
      body: JSON.stringify(input)
    }),
  logout: () => request<{ loggedOut: boolean }>("/api/auth/logout", { method: "POST" }),
  clients: () => request<ClientDTO[]>("/api/clients"),
  createClient: (input: { name: string; email?: string | null; phone?: string | null; notes?: string | null }) =>
    request<ClientDTO>("/api/clients", {
      method: "POST",
      body: JSON.stringify(input)
    }),
  updateClient: (id: string, input: { name?: string; email?: string | null; phone?: string | null; notes?: string | null; status?: string }) =>
    request<ClientDTO>(`/api/clients/${id}`, {
      method: "PUT",
      body: JSON.stringify(input)
    }),
  serviceTargets: () => request<ServiceTargetDTO[]>("/api/service-targets"),
  createServiceTarget: (input: { clientId: string; type: string; label: string; address?: string | null; details?: Record<string, unknown>; status?: string }) =>
    request<ServiceTargetDTO>("/api/service-targets", {
      method: "POST",
      body: JSON.stringify(input)
    }),
  updateServiceTarget: (id: string, input: { clientId?: string; type?: string; label?: string; address?: string | null; details?: Record<string, unknown>; status?: string }) =>
    request<ServiceTargetDTO>(`/api/service-targets/${id}`, {
      method: "PUT",
      body: JSON.stringify(input)
    }),
  leads: () => request<LeadDTO[]>("/api/leads"),
  createLead: (input: {
    clientId?: string | null;
    targetId?: string | null;
    source?: string;
    sourceDetail?: string | null;
    summary: string;
    conversationText?: string | null;
    status?: string;
    quotedCents?: number | null;
    scheduledAt?: string | null;
  }) =>
    request<LeadDTO>("/api/leads", {
      method: "POST",
      body: JSON.stringify(input)
    }),
  updateLead: (id: string, input: {
    clientId?: string | null;
    targetId?: string | null;
    source?: string;
    sourceDetail?: string | null;
    summary?: string;
    conversationText?: string | null;
    status?: string;
    quotedCents?: number | null;
    scheduledAt?: string | null;
  }) =>
    request<LeadDTO>(`/api/leads/${id}`, {
      method: "PUT",
      body: JSON.stringify(input)
    }),
  analyzeLeadConversation: (leadId: string) =>
    request<LeadConversationAnalysisDTO>(`/api/leads/${leadId}/conversation-analysis`, {
      method: "POST"
    }),
  quotePackages: () => request<QuotePackageDTO[]>("/api/quote-packages"),
  createQuotePackage: (input: { name: string; description?: string | null; basePriceCents: number; sortOrder?: number; status?: string }) =>
    request<QuotePackageDTO>("/api/quote-packages", {
      method: "POST",
      body: JSON.stringify(input)
    }),
  updateQuotePackage: (id: string, input: { name?: string; description?: string | null; basePriceCents?: number; sortOrder?: number; status?: string }) =>
    request<QuotePackageDTO>(`/api/quote-packages/${id}`, {
      method: "PUT",
      body: JSON.stringify(input)
    }),
  quotes: () => request<QuoteDTO[]>("/api/quotes"),
  quote: (id: string) => request<QuoteDTO>(`/api/quotes/${id}`),
  leadQuote: (leadId: string) => request<QuoteDTO | null>(`/api/leads/${leadId}/quote`),
  createLeadQuoteDraft: (leadId: string, input: {
    packageId: string;
    title?: string | null;
    notes?: string | null;
    scheduledStart?: string | null;
    scheduledEnd?: string | null;
    addOns?: Array<{ name: string; itemType?: string; quantity?: number; unitPriceCents: number; sortOrder?: number }>;
  }) =>
    request<QuoteDTO>(`/api/leads/${leadId}/quote`, {
      method: "POST",
      body: JSON.stringify(input)
    }),
  sendQuote: (id: string) => request<QuoteDTO>(`/api/quotes/${id}/send`, { method: "POST" }),
  acceptQuote: (id: string, input: { scheduledStart?: string | null; scheduledEnd?: string | null; jobTitle?: string | null; jobNotes?: string | null }) =>
    request<{ quote: QuoteDTO; job: JobDTO; appointment: AppointmentDTO }>(`/api/quotes/${id}/accept`, {
      method: "POST",
      body: JSON.stringify(input)
    }),
  jobs: () => request<JobDTO[]>("/api/jobs"),
  jobChecklist: (jobId: string) => request<JobChecklistItemDTO[]>(`/api/jobs/${jobId}/checklist`),
  createJobChecklistItem: (jobId: string, input: { label: string; notes?: string | null; sortOrder?: number; status?: string }) =>
    request<JobChecklistItemDTO>(`/api/jobs/${jobId}/checklist-items`, {
      method: "POST",
      body: JSON.stringify(input)
    }),
  updateJobChecklistItem: (id: string, input: {
    label?: string;
    notes?: string | null;
    sortOrder?: number;
    status?: string;
    completedAt?: string | null;
  }) =>
    request<JobChecklistItemDTO>(`/api/job-checklist-items/${id}`, {
      method: "PUT",
      body: JSON.stringify(input)
    }),
  deleteJobChecklistItem: (id: string) =>
    request<{ deleted: boolean }>(`/api/job-checklist-items/${id}`, {
      method: "DELETE"
    }),
  invoices: () => request<InvoiceDTO[]>("/api/invoices"),
  createInvoiceForJob: (jobId: string, input: { amountCents: number; externalPaymentRef?: string | null }) =>
    request<InvoiceDTO>(`/api/jobs/${jobId}/invoice`, {
      method: "POST",
      body: JSON.stringify(input)
    }),
  updateInvoice: (id: string, input: {
    amountCents?: number;
    status?: string;
    externalPaymentRef?: string | null;
    sentAt?: string | null;
    paidAt?: string | null;
  }) =>
    request<InvoiceDTO>(`/api/invoices/${id}`, {
      method: "PUT",
      body: JSON.stringify(input)
    }),
  jobMedia: (jobId: string) => request<MediaAssetDTO[]>(`/api/jobs/${jobId}/media`),
  uploadJobMedia: (jobId: string, input: { file: File; assetGroup: string; label?: string }) => {
    const form = new FormData();
    form.set("file", input.file);
    form.set("assetGroup", input.assetGroup);
    if (input.label) form.set("label", input.label);
    return request<MediaAssetDTO>(`/api/jobs/${jobId}/media`, {
      method: "POST",
      body: form
    });
  },
  updateMediaAsset: (id: string, input: { assetGroup?: string; label?: string | null; status?: string }) =>
    request<MediaAssetDTO>(`/api/media-assets/${id}`, {
      method: "PUT",
      body: JSON.stringify(input)
    }),
  archiveMediaAsset: (id: string) => request<MediaAssetDTO>(`/api/media-assets/${id}`, { method: "DELETE" }),
  mediaAssetBlob: (id: string) => requestBlob(`/api/media-assets/${id}/content`)
};

export const adminApi = {
  me: () => request<AdminMeDTO>("/api/admin/auth/me"),
  startOtp: (email: string) => request<{ sent: boolean; devCode?: string }>("/api/admin/auth/otp/start", {
    method: "POST",
    body: JSON.stringify({ email })
  }),
  verifyOtp: (email: string, code: string, name?: string) => request<AdminMeDTO>("/api/admin/auth/otp/verify", {
    method: "POST",
    body: JSON.stringify({ email, code, ...(name ? { name } : {}) })
  }),
  logout: () => request<{ loggedOut: boolean }>("/api/admin/auth/logout", { method: "POST" }),
  marketingContacts: (reviewStatus?: string) => request<MarketingContactDTO[]>(`/api/admin/marketing-contacts${reviewStatus ? `?reviewStatus=${encodeURIComponent(reviewStatus)}` : ""}`),
  findMarketingContactEmail: (id: string) =>
    request<MarketingContactEmailDiscoveryDTO>(`/api/admin/marketing-contacts/${id}/find-email`, {
      method: "POST"
    }),
  updateMarketingContact: (id: string, input: { phone?: string | null; email?: string | null }) =>
    request<MarketingContactDTO>(`/api/admin/marketing-contacts/${id}`, {
      method: "PUT",
      body: JSON.stringify(input)
    }),
  reviewMarketingContact: (id: string, input: { reviewStatus: string; outreachStatus?: string; consentStatus?: string; nextAction?: string | null }) =>
    request<MarketingContactDTO>(`/api/admin/marketing-contacts/${id}/review`, {
      method: "PUT",
      body: JSON.stringify(input)
    })
};
